import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import { Row, Col } from 'react-bootstrap';
import { motion } from "framer-motion";
import "./index.scss";

import NoDataFound from "../../../../../components/ui/NoDataFound";
import PaginationArrow from "../../../../../components/ui/PaginationArrow";
import SkeletonLoader from "../../../../../components/ui/SkeletonLoader";

import DomainInformation from "../../../../../components/PostLogin/ScanInfo/ExposureManagement/AssetDiscovery/AssociatedDomain/DomainInformation";
import SSLCertificate from "../../../../../components/PostLogin/ScanInfo/ExposureManagement/AssetDiscovery/AssociatedDomain/SSLCertificate";
import DNSRecordInformation from "../../../../../components/PostLogin/ScanInfo/ExposureManagement/AssetDiscovery/SubDomain/DNSRecordInformation";

import ListFilter from "../../../../../components/PostLogin/ScanInfo/ExposureManagement/ConcerningExposure/ListFilter";

import { searchIcon } from "../../../../../utils/icons";
import { formatNumber } from "../../../../../utils/util";

import { useScanInfoContext } from "../../../../ScanInfo/ScanInfoContext";

import { useSelector, useDispatch } from 'react-redux';
import { getSimilarDomainList, getSimilarDomainSSL, getScanInfoStats, resetScanInfoStats } from "../../../../../store/actions/scanInfoAction";

import apiEndpoints from "../../../../../apiEndpoints";

const SimilarDoamin = () => {

    const similarDomainListResponse = useSelector((state) => state.scanInfo?.similarDomainList);
    const similarDomainSSLResponse = useSelector((state) => state?.scanInfo?.similarDomainSSL);
    const similarDomainStatsResponse = useSelector((state) => state.scanInfo?.scanInfoStats);

    const dispatch = useDispatch();

    const {
        domainId,
        scanId,
        organizationId,
        triggerStatsFetch,
        setTriggerStatsFetch,
        triggerListFetch,
        setTriggerListFetch,
        setContextCurrentPage
    } = useScanInfoContext();

    const [activeTab, setActiveTab] = useState("domainInformation");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedSimilarDomain, setSelectedSimilarDomain] = useState("");
    const [isSSLloading, setIsSSLloading] = useState(false);
    const [scanNotFound, setScanNotFound] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [similarDomainStats, setSimilarDomainStats] = useState("");
    const [statsLoading, setStatsLoading] = useState(false);

    //list search state
    const [listSearch, setListSearch] = useState("");
    const [updatedListSearchValue, setUpdatedListSearchValue] = useState("");

    //selected list filter
    const [listFilterValue, setListFilterValue] = useState({});
    
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        setContextCurrentPage(newPage);
        setTriggerListFetch(true);
    };

    const listFilterApply = (data) => {
        setCurrentPage(1);
        setListFilterValue(data);
        setContextCurrentPage(1);
        setTriggerListFetch(true);
    }

    const isAnyFilterActive = Object.values(listFilterValue).some(value => Boolean(value));

    const resetFilterValue = () => {
        setCurrentPage(1);
        setListFilterValue({});
        setContextCurrentPage(1);
        setTriggerListFetch(true);
    }

    const fetchSimilarDomainStats = useCallback(()=>{
        setStatsLoading(true)
        const baseUrl = `${apiEndpoints.scanInfo.exposureManagemnet.brandInsight.similarDomain.similarDomainStats()}`
        const params = organizationId.toLowerCase() !== "all" && domainId.toLowerCase() === "all" ? `&organization_id=${organizationId}`: `&domain=${domainId}&scan_id=${scanId}`
        const url = `${baseUrl}${params}`
        dispatch(getScanInfoStats(url))
    },[dispatch, domainId, scanId, organizationId])

    const requestSearch = useMemo(() => debounce((searchTerm) => {        
        setUpdatedListSearchValue(searchTerm)
    }, 500), []);

    const fetchSimilarDomainList = useCallback(()=>{
        setIsLoading(true);
        setScanNotFound(false);
        const baseUrl = `&page=${currentPage}&page_size=10`
        const params = organizationId.toLowerCase() !== "all" && domainId.toLowerCase() === "all" ? `&organization_id=${organizationId}`: `&domain=${domainId}&scan_id=${scanId}`
        const queryParams = updatedListSearchValue ? `&query=${updatedListSearchValue}` : "";
        const newFindings = listFilterValue?.newFindings?.value && listFilterValue?.newFindings?.value !== "all" ? `&new_record=${listFilterValue?.newFindings?.value === "yes" ? true : false}` : "";
        const url = `${baseUrl}${params}${queryParams}${newFindings}`
        dispatch(getSimilarDomainList(url))
    },[dispatch, scanId, domainId, organizationId, currentPage, updatedListSearchValue, listFilterValue]);

    const fetchSSLcertificate = useCallback(()=>{
        let url = `&domain=${selectedSimilarDomain?.domain}&scan_id=${selectedSimilarDomain?.scan_id}&similar_domain=${selectedSimilarDomain?.similar_domain}`
        setIsSSLloading(true);
        dispatch(getSimilarDomainSSL(url))
    },[dispatch, selectedSimilarDomain])

    useEffect(()=>{
        if(selectedSimilarDomain && activeTab === "SSlCertificate") {
            fetchSSLcertificate()
        }        
    },[selectedSimilarDomain, fetchSSLcertificate, activeTab])

    useEffect(() => {
        if (triggerStatsFetch) {
            fetchSimilarDomainStats();
            setCurrentPage(1)
            setContextCurrentPage(1)
            setTriggerStatsFetch(false);
        }
    }, [triggerStatsFetch, setContextCurrentPage, setTriggerStatsFetch, fetchSimilarDomainStats]);

    useEffect(() => {
        if (triggerListFetch) {
            fetchSimilarDomainList();
            setTriggerListFetch(false);
        }
    }, [triggerListFetch, currentPage, setTriggerListFetch, fetchSimilarDomainList]);

    useEffect(() => {
        setCurrentPage(1); // reset to first page
        setContextCurrentPage(1);
        setTriggerListFetch(true); // trigger fetch
    }, [setTriggerListFetch, setContextCurrentPage, updatedListSearchValue]);

    useEffect(()=>{        
        if(similarDomainListResponse !== null) {
            if(similarDomainListResponse?.status === "failed") {
                setScanNotFound(true)
            } else {
                setScanNotFound(false)
            }
            if(similarDomainListResponse?.results?.length > 0) {                
                setSelectedSimilarDomain(similarDomainListResponse?.results[0])
            }           
            setIsLoading(false);
        }
    },[similarDomainListResponse])

    useEffect(()=>{
        if(similarDomainSSLResponse !== null) {
            setIsSSLloading(false);
        }
    },[similarDomainSSLResponse])

    useEffect(()=>{    
        if(similarDomainStatsResponse !== null) {
            setStatsLoading(false)
            setSimilarDomainStats(similarDomainStatsResponse)
            dispatch(resetScanInfoStats())
        }
    },[similarDomainStatsResponse, dispatch])


    return (
        <>
            <div className="scan-info-main-content sensitive-exposure-main-content">
                <div className="bg-white-with-shadow p-4">
                    <Row>                        
                        <Col lg={4}>
                            {
                                statsLoading ?
                                <SkeletonLoader type={"line"} count={3} currentPage={"similarDomain"} />
                                :
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <div className="discover-doamin-container associated-doamin-page rounded p-3">
                                        <div className="header-content d-flex justify-content-between">
                                            <p className="heading">Similar Domains</p>
                                        </div>
                                        <div className="d-flex gap-3">
                                            <div className="total-score-container rounded">
                                                <div className="users-totol-count d-flex">
                                                    <p className="total-score-count">{formatNumber(similarDomainStats?.record_type?.total)}</p>
                                                    <div className="score-count mx-3">
                                                        <span className="new-record d-inline-block mx-1"></span>
                                                        <span className="existing-record d-inline-block"></span>
                                                        <p>Total Records</p>
                                                    </div>
                                                </div>
                                                <div className="new-exist-count d-flex gap-5 mt-3">
                                                    <p className="new-record"><span className="d-inline-block">{formatNumber(similarDomainStats?.record_type?.new_record)}</span> New</p>
                                                    <p className="existing-record"><span className="d-inline-block">{formatNumber(similarDomainStats?.record_type?.existing_record)}</span> Existing</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            }                            
                        </Col>
                    </Row>
                </div>
                <div className="mt-4 js-secrets-main-content">
                    <Row>
                        <Col lg={3}>
                            <div className="bg-white-with-shadow subdomain-list-container h-100">
                                <div className="list-heading px-3 pt-3 d-flex justify-content-between align-items-center">
                                    <h1>Similar Domain List</h1>
                                    {isAnyFilterActive && (<span className="reset-filter-link" onClick={resetFilterValue}>Reset Filter</span>)}
                                    <ListFilter listFilterApply={listFilterApply} listFilterValue={listFilterValue} currentPage={"similarDomain"} />
                                </div>
                                <div className="search-container px-3 position-relative">
                                    <span className="position-absolute search-icon">{searchIcon()}</span>
                                    <input type="text" 
                                        placeholder="Search" 
                                        className="w-100 rounded input"
                                        value={listSearch}
                                        onChange={(e)=>{                                            
                                            setListSearch(e.target.value);
                                            requestSearch(e.target.value);
                                            setCurrentPage(1);
                                            setContextCurrentPage(1);
                                            setTriggerListFetch(true);
                                        }}
                                    />
                                </div>
                                <div className="list-main-container js-secrets-list sensitive-exposure-list">
                                    { !isLoading && scanNotFound &&
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4 }}
                                        >
                                            <NoDataFound noDataText={similarDomainListResponse?.description}/>
                                        </motion.div>
                                    }
                                    {
                                        isLoading && !scanNotFound ?
                                        <SkeletonLoader type={"line"} count={5} currentPage={"similarDomain"} />
                                        :
                                        (
                                            similarDomainListResponse?.results?.length > 0 &&
                                            similarDomainListResponse?.results?.map((data, index) => {
                                                return (
                                                    <motion.div
                                                        key={index}
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.4, delay: index * 0.1 }}
                                                    >
                                                        <div className={`sub-domain-list py-2 px-3 ${selectedSimilarDomain?.id === data?.id ? "active" : ""}`} key={index} onClick={()=>{setSelectedSimilarDomain(data)}}>
                                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                                <p className="sub-domain-heading">{data?.similar_domain}</p>
                                                                {data?.new_record && <span className="new-subdomain">New</span>}
                                                            </div>
                                                            <p className="sub-domain-name">{data?.domain}</p>
                                                            <div className="status-container d-flex gap-4 mb-2">
                                                                <p className="severity">MX Record&nbsp;: &nbsp;  
                                                                    {data?.mx_record === null && <span>-</span>}
                                                                    {data?.mx_record === true && <span className="critical">Yes</span>}
                                                                    {data?.mx_record === false && <span className="low">No</span>}
                                                                </p>
                                                                <p className="severity">SPF Record&nbsp;: &nbsp;  
                                                                    {data?.spf_record === null && <span>-</span>}
                                                                    {data?.spf_record === true && <span className="critical">Yes</span>}
                                                                    {data?.spf_record === false&& <span className="low">No</span>}
                                                                </p>
                                                            </div>
                                                            <p className={`average-percentage ${data?.additional_info?.similarity_score?.overall >= 50 ? "red" : "green"}`}>Similarity : {data?.additional_info?.similarity_score?.overall}%</p>
                                                            {data?.web_risk?.is_web_risk && 
                                                                data?.web_risk?.is_web_risk?.web_risk_data.length >  0 &&
                                                                data?.web_risk?.is_web_risk?.web_risk_data.map((data)=>{
                                                                    return (
                                                                        <span className="subdomain-status blacklisted">{data}</span>
                                                                    )
                                                                })                                                        
                                                            }
                                                            {data?.additional_info?.blacklisted?.is_blacklisted &&  <span className="subdomain-status blacklisted">Blacklisted</span>}
                                                            {/* <span className="potential">Self Signed</span> */}
                                                        </div>
                                                    </motion.div>
                                                )
                                            })
                                        )
                                    }
                                    { !isLoading && similarDomainListResponse?.results?.length > 0 &&
                                        <PaginationArrow 
                                            totalCount={similarDomainListResponse?.meta?.total_results}
                                            currentPage={currentPage}
                                            onPageChange={handlePageChange}
                                        />
                                    }
                                    { !isLoading && similarDomainListResponse?.results?.length === 0 &&
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4 }}
                                        >
                                            <NoDataFound noDataText={"No Data Found"}/>
                                        </motion.div>
                                    }                                    
                                </div>
                            </div>
                        </Col>
                        <Col lg={9}>
                            <div className="bg-white-with-shadow sub-domain-main-container p-3 h-100">
                                <div className="tab-container d-flex rounded">
                                    <p className={`rounded text-center ${activeTab === "domainInformation" ? "active" : ""}`} onClick={()=>{setActiveTab('domainInformation')}}>Domain Information</p>
                                    <p className={`rounded text-center ${activeTab === "DNSRecordInformation" ? "active" : ""}`} onClick={()=>{setActiveTab('DNSRecordInformation')}}>DNS Record Information</p>
                                    <p className={`rounded text-center ${activeTab === "SSlCertificate" ? "active" : ""}`} onClick={()=>{setActiveTab('SSlCertificate')}}>SSL Certificate</p>
                                </div>
                                <div className="tab-details-content mt-3">
                                    {activeTab === "domainInformation" &&
                                        <>  {
                                            isLoading ?
                                                <SkeletonLoader type={"line"} count={5} currentPage={"similarDomain"} />
                                            :
                                            <>
                                                {similarDomainListResponse?.results?.length === 0 ?
                                                    <motion.div
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.4 }}
                                                    >
                                                        <NoDataFound noDataText={"Please select Any similar Domain"}/>
                                                    </motion.div>
                                                    :
                                                    <motion.div
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.4 }}
                                                    >
                                                        <DomainInformation SimilarDomainData={selectedSimilarDomain} currentPageType={"similarDomain"}/>
                                                    </motion.div>
                                                }
                                            </>                                            
                                            }
                                        </>
                                    }
                                    {activeTab === "SSlCertificate" && 
                                        <>  {
                                                !isSSLloading && similarDomainListResponse?.results?.length === 0 ?
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.4 }}
                                                >
                                                    <NoDataFound noDataText={"Please select Any similar Domain"}/> 
                                                </motion.div>
                                                :
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.4 }}
                                                >
                                                    <SSLCertificate sslCertificateData={similarDomainSSLResponse} isLoading={isSSLloading} />
                                                </motion.div>
                                            }
                                            
                                        </>
                                    }
                                    {activeTab === "DNSRecordInformation" && 
                                        <>  {
                                                !isSSLloading && similarDomainListResponse?.results?.length === 0 ?
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.4 }}
                                                >
                                                    <NoDataFound noDataText={"Please select Any similar Domain"}/> 
                                                </motion.div>                                                
                                                :
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.4 }}
                                                >
                                                    <DNSRecordInformation selectedSubdomain={selectedSimilarDomain} domainId={domainId} scanId={scanId} organizationId={organizationId} currentPageType={"similarDomain"}/>
                                                </motion.div>
                                            }                                            
                                        </>
                                    }
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
        </>        
    )
}


export default SimilarDoamin;